<template>
  <div class="workspace-switcher market-selection">
    <div v-if="loadingInitial" class="workspace-switcher__label">
      <v-progress-circular indeterminate size="16" width="2" class="mr-1"></v-progress-circular>
      <span>Workspace...</span>
    </div>
    <div v-else-if="error" class="workspace-switcher__label">
      <span>Workspace</span>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon small color="error" v-bind="attrs" v-on="on">mdi-alert-circle</v-icon>
        </template>
        <span>{{ error }}</span>
      </v-tooltip>
    </div>
    <div v-else-if="workspaces.length === 1" class="workspace-switcher__label">
      <v-icon small class="mr-1">mdi-office-building</v-icon>
      <span>{{ currentWorkspaceName }}</span>
    </div>
    <div v-else-if="workspaces.length === 0" class="workspace-switcher__label">
      <v-icon small class="mr-1">mdi-office-building</v-icon>
      <span>Workspace</span>
    </div>
    <template v-else>
      <v-select
        id="workspace-select"
        v-model="selectedWorkspace"
        :items="workspaces"
        item-text="tenantName"
        item-value="tenantId"
        placeholder="Workspace"
        dense
        hide-details
        :disabled="isLoading"
        prepend-inner-icon="mdi-office-building"
        aria-label="Select workspace"
        @change="handleWorkspaceChange"
      >
        <template v-slot:selection="{ item }">
          <span>{{ item.tenantName }}{{ item.isPrimary ? " (Primary)" : "" }}</span>
        </template>
        <template v-slot:item="{ item }">
          <span>{{ item.tenantName }}{{ item.isPrimary ? " (Primary)" : "" }}</span>
        </template>
      </v-select>
      <div v-if="isLoading" class="workspace-switcher__loading">
        <v-progress-circular indeterminate size="16" width="2"></v-progress-circular>
        <span>Switching workspace...</span>
      </div>
    </template>
  </div>
</template>

<script>
import { workspaceService } from "@/services/workspaceService";
import { setWorkspaceSwitchInProgress } from "@/services/workspaceCrossAppSync";

const IS_DEV = process.env.NODE_ENV === "development";
function debugLog() {
  if (!IS_DEV) return;
  console.log.apply(console, arguments);
}

export default {
  name: "WorkspaceSwitcher",
  data() {
    return {
      workspaces: [],
      selectedWorkspace: null,
      isLoading: false,
      loadingInitial: true,
      error: null,
      profileSyncTimer: null,
    };
  },
  computed: {
    accessToken() {
      return this.$store.state.authentication?.accessToken || "";
    },
    /** OIDC profile tenant — updates after signinSilent even when workspace-changed is missed (e.g. other origin app). */
    profileSelectedTenant() {
      var u = this.$store.state.authentication.user;
      if (!u || !u.profile || u.profile.selected_tenant == null) return null;
      var t = String(u.profile.selected_tenant).trim();
      return t !== "" ? t : null;
    },
    currentWorkspaceName() {
      if (this.workspaces.length !== 1) return "";
      const w = this.workspaces[0];
      return (w && w.tenantName) || this.selectedWorkspace || "Workspace";
    },
    /** Direct Vuex store tenant — updated by workspace/loadWorkspace (App.vue workspace-changed handler). */
    vuexCurrentTenantId() {
      const id = this.$store.state.workspace && this.$store.state.workspace.currentTenantId;
      return id != null ? String(id).trim() : null;
    },
  },
  watch: {
    /**
     * Keep selectedWorkspace in sync with the Vuex store when App.vue's workspace-changed
     * handler commits the new tenant via workspace/loadWorkspace.
     * This prevents the dropdown from showing a stale tenant when only the market updated.
     */
    vuexCurrentTenantId: function (newVal) {
      if (!newVal) return;
      if (String(this.selectedWorkspace || "").trim() === newVal) return;
      var inList = this.workspaces.some(function (w) {
        return w && String(w.tenantId || "").trim() === newVal;
      });
      if (inList) {
        this.selectedWorkspace = newVal;
      }
    },
    /**
     * When workspaces list refreshes, re-check the Vuex tenant and sync if needed.
     * Handles the race where vuexCurrentTenantId was set before workspaces were loaded.
     */
    workspaces: function (newVal) {
      var stored = this.$store.state.workspace && this.$store.state.workspace.currentTenantId;
      if (!stored) return;
      var normalized = String(stored).trim();
      if (!normalized) return;
      if (String(this.selectedWorkspace || "").trim() === normalized) return;
      var inList = Array.isArray(newVal) && newVal.some(function (w) {
        return w && String(w.tenantId || "").trim() === normalized;
      });
      if (inList) {
        this.selectedWorkspace = normalized;
      }
    },
    profileSelectedTenant: function (newVal) {
      if (this.loadingInitial || this.isLoading) return;
      if (newVal == null) return;
      if (String(this.selectedWorkspace || "").trim() === newVal) return;
      var self = this;
      if (this.profileSyncTimer) clearTimeout(this.profileSyncTimer);
      this.profileSyncTimer = setTimeout(function () {
        self.profileSyncTimer = null;
        self.loadWorkspaces(undefined, newVal, { silent: true });
      }, 300);
    },
  },
  mounted() {
    this.loadWorkspaces();
    this._onWorkspaceChanged = (event) => {
      const detail = event && event.detail;
      const raw =
        detail &&
        (detail.selectedTenantId != null && String(detail.selectedTenantId).trim() !== ""
          ? String(detail.selectedTenantId).trim()
          : detail.correctTenantId != null && String(detail.correctTenantId).trim() !== ""
            ? String(detail.correctTenantId).trim()
            : "");
      const selectedTenantId = raw !== "" ? raw : undefined;
      // Silent: do not toggle loadingInitial — avoids v-select flicker on every poll refresh.
      this.loadWorkspaces(undefined, selectedTenantId, { silent: true });
    };
    window.addEventListener("workspace-changed", this._onWorkspaceChanged);
  },
  beforeDestroy() {
    if (this.profileSyncTimer) {
      clearTimeout(this.profileSyncTimer);
      this.profileSyncTimer = null;
    }
    if (this._onWorkspaceChanged) {
      window.removeEventListener("workspace-changed", this._onWorkspaceChanged);
    }
  },
  methods: {
    normalizeWorkspaces(items) {
      if (!Array.isArray(items) || items.length === 0) {
        return [];
      }

      const byTenantId = new Map();
      for (const item of items) {
        const tenantId =
          item && item.tenantId != null ? String(item.tenantId).trim() : "";
        if (!tenantId) {
          continue;
        }

        const existing = byTenantId.get(tenantId);
        const normalized = {
          ...item,
          tenantId,
          tenantName:
            item && item.tenantName != null && String(item.tenantName).trim() !== ""
              ? String(item.tenantName)
              : tenantId,
          isPrimary: !!(item && item.isPrimary),
        };

        if (!existing) {
          byTenantId.set(tenantId, normalized);
          continue;
        }

        if (!existing.isPrimary && normalized.isPrimary) {
          byTenantId.set(tenantId, normalized);
        }
      }

      return Array.from(byTenantId.values());
    },

    /**
     * @param {string} [tokenOverride]
     * @param {string} [currentTenantIdOverride]
     * @param {{ silent?: boolean }} [options] — silent=true: refresh list/selection without full spinner (workspace-changed / background sync)
     */
    async loadWorkspaces(tokenOverride, currentTenantIdOverride, options) {
      var silent = options && options.silent === true;
      this.error = null;
      if (!silent) {
        this.loadingInitial = true;
      }
      try {
        const token =
          tokenOverride != null && tokenOverride !== "" ? tokenOverride : undefined;
        const available = await workspaceService.getAvailableWorkspaces(token);
        const normalizedWorkspaces = this.normalizeWorkspaces(available || []);
        this.workspaces = normalizedWorkspaces;

        let selectedTenantId = currentTenantIdOverride;
        if (selectedTenantId == null || selectedTenantId === "") {
          const current = await workspaceService.getCurrentWorkspace(token);
          selectedTenantId = (current && current.selectedTenantId) || null;
        }

        this.selectedWorkspace = selectedTenantId;

        if (!this.selectedWorkspace && this.workspaces.length > 0) {
          const primary = this.workspaces.find((w) => w.isPrimary);
          this.selectedWorkspace = primary
            ? primary.tenantId
            : this.workspaces[0].tenantId;
        }

        this.$store.commit("workspace/SET_AVAILABLE_WORKSPACES", this.workspaces);
        const currentWorkspace = this.workspaces.find(
          (w) => w.tenantId === this.selectedWorkspace
        );
        this.$store.commit("workspace/SET_CURRENT_WORKSPACE", {
          tenantId: this.selectedWorkspace,
          tenantName: (currentWorkspace && currentWorkspace.tenantName) || this.selectedWorkspace,
        });

        const pendingSwitch = sessionStorage.getItem("workspace_switch_pending");
        if (pendingSwitch) {
          sessionStorage.removeItem("workspace_switch_pending");
          if (this.selectedWorkspace !== pendingSwitch) {
            this.error =
              "Workspace switch could not be applied. The Identity Server must return access_token from the workspace switch API.";
          }
        }
      } catch (err) {
        console.error("Failed to load workspaces:", err);
        this.error = err.message || "Failed to load workspaces";
        this.workspaces = [];
      } finally {
        if (!silent) {
          this.loadingInitial = false;
        }
      }
    },
    async handleWorkspaceChange() {
      const newTenantId = this.selectedWorkspace;
      if (!newTenantId) return;

      this.isLoading = true;
      this.error = null;

      try {
        setWorkspaceSwitchInProgress(true);
        const result = await workspaceService.switchWorkspace(newTenantId, undefined);
        // 409: another switch just happened (multi-tab). Never call /switch again from here — sync UI + JWT.
        if (result && result.cooldown && result.currentTenantId) {
          debugLog(
            "[WorkspaceSwitcher] Switch cooldown — already on",
            result.currentTenantId
          );
          try {
            const { getUserManager } = await import("@/js/authentication/authentication");
            const um = getUserManager();
            if (um && typeof um.signinSilent === "function") {
              await um.signinSilent();
            }
          } catch (e) {
            debugLog("[WorkspaceSwitcher] Silent renew after cooldown:", e);
          }
          await this.$store.dispatch("authentication/updateLoggedInStatus");
          await this.$store.dispatch("workspace/loadWorkspace");
          this.$store.dispatch("assetxpstate/selectMarket", null);
          this.$store.dispatch("assetxpstate/ResetSelectedSection");
          this.$store.dispatch("assetxpstate/ResetSelectedAsset");
          this.$store.dispatch("assetxpstate/ClearSectionAndSearchStateForWorkspaceSwitch");
          await this.$store.dispatch("assetxpstate/GetMyMarkets");
          // Restore only an explicit selected_market claim for the switched workspace.
          // Do not fall back to defaultmarket here because it can override persisted market choice.
          const cooldownProfile = this.$store.state.authentication.user?.profile;
          const cooldownMarketId = (cooldownProfile?.selected_market || "").toString().trim();
          const cooldownAvailableMarkets = this.$store.state.assetxpstate.markets || [];
          debugLog("[MarketTrace][WorkspaceSwitcher] cooldown restore check", {
            selectedTenant: cooldownProfile?.selected_tenant || null,
            tokenSelectedMarket: cooldownProfile?.selected_market || null,
            selectedMarketInStore: this.$store.state.assetxpstate.selectedMarketId || null,
            markets: cooldownAvailableMarkets.map((m) => (m && m.id) || null),
          });
          if (cooldownMarketId && cooldownAvailableMarkets.some((m) => m && String(m.id).trim() === cooldownMarketId)) {
            this.$store.commit("assetxpstate/SetSelectedMarket", {
              marketId: cooldownMarketId,
              source: "workspace-switch-token",
            });
            debugLog("[MarketTrace][WorkspaceSwitcher] cooldown restored token market", {
              selectedTenant: cooldownProfile?.selected_tenant || null,
              appliedMarketId: cooldownMarketId,
            });
          }
          await this.$store.dispatch("authentication/updateMarketPermissions");
          this.$router.push("/").catch(() => {});
          this.loadWorkspaces(undefined, result.currentTenantId, { silent: true });
          return;
        }
        if (result && result.tokenApplied) {
          await this.$store.dispatch("authentication/updateLoggedInStatus");
          await this.$store.dispatch("workspace/loadWorkspace");
          this.$store.dispatch("assetxpstate/selectMarket", null);
          this.$store.dispatch("assetxpstate/ResetSelectedSection");
          this.$store.dispatch("assetxpstate/ResetSelectedAsset");
          this.$store.dispatch("assetxpstate/ClearSectionAndSearchStateForWorkspaceSwitch");
          await this.$store.dispatch("assetxpstate/GetMyMarkets");
          // Restore only an explicit selected_market claim for the switched workspace.
          // Do not fall back to defaultmarket here because it can override persisted market choice.
          const switchProfile = this.$store.state.authentication.user?.profile;
          const switchMarketId = (switchProfile?.selected_market || "").toString().trim();
          const switchAvailableMarkets = this.$store.state.assetxpstate.markets || [];
          debugLog("[MarketTrace][WorkspaceSwitcher] tokenApplied restore check", {
            selectedTenant: switchProfile?.selected_tenant || null,
            tokenSelectedMarket: switchProfile?.selected_market || null,
            selectedMarketInStore: this.$store.state.assetxpstate.selectedMarketId || null,
            markets: switchAvailableMarkets.map((m) => (m && m.id) || null),
          });
          if (switchMarketId && switchAvailableMarkets.some((m) => m && String(m.id).trim() === switchMarketId)) {
            this.$store.commit("assetxpstate/SetSelectedMarket", {
              marketId: switchMarketId,
              source: "workspace-switch-token",
            });
            debugLog("[MarketTrace][WorkspaceSwitcher] tokenApplied restored token market", {
              selectedTenant: switchProfile?.selected_tenant || null,
              appliedMarketId: switchMarketId,
            });
          }
          await this.$store.dispatch("authentication/updateMarketPermissions");
          this.$router.push("/").catch(() => {});
          const switchedTenantId = result.tenantId != null ? result.tenantId : newTenantId;
          this.loadWorkspaces(result.newAccessToken, switchedTenantId, { silent: true });
        }
      } catch (err) {
        console.error("Failed to switch workspace:", err);
        this.error = err.message || "Failed to switch workspace";
        const current = await workspaceService.getCurrentWorkspace(undefined).catch(() => null);
        this.selectedWorkspace =
          (current && current.selectedTenantId) || this.workspaces[0]?.tenantId;
      } finally {
        setWorkspaceSwitchInProgress(false);
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.workspace-switcher {
  position: relative;
  display: inline-flex;
  align-items: center;
  /* Width matches app bar market selector (see MarketSelection.scss + .appbar-workspace-switcher) */
  min-width: 160px;
  max-width: 220px;
  width: 100%;
  box-sizing: border-box;
}

.workspace-switcher__label {
  display: inline-flex;
  align-items: center;
  font-size: 0.875rem;
  min-height: 38px;
  width: 100%;
  box-sizing: border-box;
}

.workspace-switcher__loading {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 0.25rem;
  padding: 0.5rem;
  font-size: 0.75rem;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Keep dropdown control height aligned with MarketSelection in the app bar */
.appbar-workspace-switcher /deep/ .v-input__slot {
  min-height: 38px;
}

@media (max-width: 960px) {
  .appbar-workspace-switcher /deep/ .v-input__slot {
    min-height: 34px;
  }
}

@media (max-width: 600px) {
  .appbar-workspace-switcher /deep/ .v-input__slot {
    min-height: 32px;
  }
}
</style>
